// Fragments used only by the new builder (DBO-1436). The old recommends
// feature keeps its own fragments (OVPot, OVRecommendation) in
// src/graphql/fragments.ts — plan rows there come from `pots`, where pot is
// nullable because rows can be backed by a bank account instead. The builder
// reads `potPlans` (pot guaranteed non-null); bank-account withdrawal
// destinations are read from `withdraw.destination`.
import {gql} from '@apollo/client';
import {
  FEE_FRAGMENT,
  MODEL_PORTFOLIO_FRAGMENT,
  OVRPOT_POT_FRAGMENT,
  RECOMMENDS_FRAGMENT,
} from '../../graphql/fragments';
import {BUILDER_BANK_ACCOUNT, BUILDER_CASH_POT, BUILDER_POT} from './builderPotFragments';

// Mirrors OVPot but on OVRecommendPotPlanType, whose rows are guaranteed
// pot-backed: pot is non-null and there are no bank-account rows, so
// consumers don't need optional chaining or potBankUnion.
export const OV_POT_PLAN_FRAGMENT = gql`
  ${OVRPOT_POT_FRAGMENT}
  ${FEE_FRAGMENT}
  ${MODEL_PORTFOLIO_FRAGMENT}
  fragment OVPotPlan on OVRecommendPotPlanType {
    id
    full
    planType
    recurringClientAmount
    recurringClientFrequency
    singleClientAmount
    singleEmployerAmount
    recurringEmployerAmount
    recurringEmployerFrequency
    hasFeeDeviation
    sppOngoingFpFeePercentage
    fees {
      ...FeeFragment
    }
    initialFee {
      id
    }
    initialEmployerFee {
      id
    }
    legacyInitialFee
    legacyInitialEmployerFee
    inSpecie
    mandate
    modelPortfolio {
      ...ModelPortfolioFragment
    }
    pot {
      ...OVRPot_Pot
    }
    adhocFeeType
    adhocFeeAmount
    adhocFeeDate
    adhocFeeReason
    recommendations {
      id
    }
    annuityType
    incomeFrequency
    guaranteedPeriod
    spouseAnnuityPercent
    rateType
    indexLinked
    ltcCapitalProtected
    protectedPercentage
    paymentType
    notes
  }
`;

// Builder twin of OVRecommendation: identical selections except plan rows
// come from potPlans (pot guaranteed non-null) instead of pots.
export const OV_BUILDER_RECOMMENDATION = gql`
  ${OV_POT_PLAN_FRAGMENT}
  ${RECOMMENDS_FRAGMENT}
  ${BUILDER_CASH_POT}
  fragment OVBuilderRecommendation on OVRecommendType {
    id
    name
    dateUpdated
    dateCreated
    solutionType
    isXo
    isSppDaXo
    isPensionSharing
    isDependantsPension
    isAps
    progressTrackerStatus
    goal {
      id
      name
    }
    selectedMandate
    review {
      id
      dateSubmitted
    }
    contact {
      id
      simplifiedName
    }
    account {
      id
    }
    productProvider {
      id
      name
      isBank
      reportName
      isSaltus
    }
    productName {
      id
      name
      reportName
    }
    recommends {
      ...RecommendsFragment
    }
    potPlans {
      ...OVPotPlan
    }
    # Minimal funding-source selection for the grid summary line: potPlans
    # can't supply it because bank-account source rows are pot-less and
    # filtered out of potPlans (DBO-1439).
    contribute {
      source {
        ... on PotType {
          id
        }
        ... on CashPotType {
          ...BuilderCashPot
        }
        ... on BankAccountType {
          id
          accountNumber
          provider {
            id
            name
            formattedName
          }
        }
      }
    }
    urgency
    urgencyReason
    isFinalised
    action
    isNewPlanRequired
    # ownership
    planType
    withdrawalReason
    withdrawalAmount
    startDate
    endDate
    isDentist
    note
    parentGuardian {
      id
    }
  }
`;

export const BUILDER_DETAILS = gql`
  ${RECOMMENDS_FRAGMENT}
  ${FEE_FRAGMENT}
  ${OV_POT_PLAN_FRAGMENT}
  ${MODEL_PORTFOLIO_FRAGMENT}
  ${BUILDER_POT}
  ${BUILDER_BANK_ACCOUNT}
  ${BUILDER_CASH_POT}
  fragment BuilderDetails on OVRecommendType {
    id
    name
    dateUpdated
    dateCreated
    solutionType
    isXo
    isSppDaXo
    progressTrackerStatus
    selectedMandate
    review {
      id
      dateSubmitted
    }
    contact {
      id
      simplifiedName
    }
    account {
      id
    }
    recommends {
      ...RecommendsFragment
    }
    urgency
    urgencyReason
    startDate
    endDate
    goal {
      id
      name
    }
    note
    action
    planType
    parentGuardian {
      id
    }
    modify {
      pot {
        ...BuilderPot
      }
      nonSaltusMandate
      modelPortfolio {
        ...ModelPortfolioFragment
      }
      fees {
        ...FeeFragment
      }
    }
    setUpNew {
      productProvider {
        id
        name
      }
      productType {
        id
        name
      }
      # mandate
      # modelPortfolio {
      #   id
      #   name
      # }

      subplans {
        ...BuilderPot
        name
        mandate
        modelPortfolio {
          ...ModelPortfolioFragment
        }
      }
    }
    protection {
      protectionType
      benefitBasis
      benefitBasisMonthlyText
      deferredPeriod
      indexation
      lifeCover
      livesAssured
      notes
      premiumType
      term
      termType
      isInTrust
      monthlyIncome
      isInvestmentLinked
      isWaiverOfPremium
      terminatingAge
      criticalIllnessCover
      lifeOrCriticalIllnessCover
    }
    annuity {
      pot {
        ...BuilderPot
      }
      isFull
      initialFee {
        id
      }
      sppInitialFee
      singleClientAmount
      notes
      annuityType
      incomeFrequency
      guaranteedPeriod
      spouseAnnuityPercent
      rateType
      isIndexLinked
      isLtcCapitalProtected
      protectedPercentage
      paymentType
    }
    contribute {
      # The source kind is carried by __typename: PotType = existing plan,
      # CashPotType = plain new cash (dummy pot), BankAccountType = new cash
      # funded from that account (DBO-1439).
      source {
        ... on PotType {
          ...BuilderPot
        }
        ... on CashPotType {
          ...BuilderCashPot
        }
        ... on BankAccountType {
          ...BuilderBankAccount
        }
      }
      employerSourceBankAccount {
        ...BuilderBankAccount
        account {
          id
          name
        }
      }
      destinationPot {
        ...BuilderPot
      }
      isFull
      isInSpecie
      singleClientAmount
      recurringClientAmount
      comboFrequency
      singleEmployerAmount
      recurringEmployerAmount
      sourceOfFunds
      employerSourceOfFunds
      initialFee {
        id
      }
      initialEmployerFee {
        id
      }
      sppInitialFee
      sppInitialEmployerFee
    }
    withdraw {
      sourcePot {
        ...BuilderPot
      }
      # Bank account destination (DBO-1436). Null for legacy saved recs whose
      # destination row holds a pot — those are read via potPlans.
      destination {
        ...BuilderBankAccount
      }
      isFull
      withdrawalAmount
      withdrawalFrequency
    }
    transfer {
      destinationPot {
        ...BuilderPot
      }
      existingSources {
        sourcePot {
          ...BuilderPot
        }
        isFull
        isInSpecie
        singleClientAmount
        initialFee {
          id
        }
        sppInitialFee
      }
      newSources {
        contact {
          id
        }
        productName {
          id
        }
        productProvider {
          id
        }
        currentPlanValue
        drawdownAmount
        isAuthorityHeld
        isDataGather
        isFull
        isInSpecie
        planNumber
        singleClientAmount
        initialFee {
          id
        }
        sppInitialFee
      }
    }
    chargeAdHocFee {
      pot {
        ...BuilderPot
      }
      adhocFeeAmount
      adhocFeeReason
      adhocFeeType
    }
    # Pot-backed plan rows only (pot guaranteed non-null). Bank-account
    # withdrawal destinations live on withdraw.destination instead.
    potPlans {
      ...OVPotPlan
    }
    isAps
    isDependantsPension
    isPensionSharing
    withdrawalReason
  }
`;
